import { action, observable, runInAction, computed } from "mobx";
import { NotificationFavoritesApiService } from "@api-services/notification-favorites/NotificationFavoritesApiService";
import { UserNotificationFavorites } from "@api-types/notification-favorites/UserNotificationFavorites";
import { isCapacitorPlatform } from "@utils/specific/capacitor/helpers";
import { UserAuthStore } from "@state/shared";

export class NotificationFavoritesStore {
	readonly throttle = 700; //ms
	readonly enableThrottling = true;

	private _updateBuffer = new Set<string>();
	private _deleteBuffer = new Set<string>();

	private _bufferTimeoutId: NodeJS.Timeout;

	@observable public userFavoriteNotificationEventSet = new Set<string>();

	@computed get shouldDisplayNotificationFavoriteButton() {
		return this.userAuthStore.isLoggedIn && isCapacitorPlatform();
	}

	userAuthStore: UserAuthStore;

	constructor(userAuthStore: UserAuthStore) {
		this.userAuthStore = userAuthStore;
	}

	@action.bound
	public toggleEventFavoriteStatus(eventId: string) {
		if (!this.userAuthStore.isLoggedIn || !isCapacitorPlatform()) {
			return;
		}

		if (this.userFavoriteNotificationEventSet.has(eventId)) {
			this.removeUserEventNotificationFavorite(eventId);
		} else {
			this.addUserEventNotificationFavorite(eventId);
		}
	}

	@action.bound
	public async getUserNotificationFavorites() {
		if (!this.userAuthStore.isLoggedIn || !isCapacitorPlatform()) {
			return;
		}

		try {
			const notificationFavorites =
				await NotificationFavoritesApiService.getNotificationFavoritesPerUser();
			this.setUserEventNotificationFavorites(
				notificationFavorites?.matchNotificationEvents || []
			);
		} catch (error) {
			console.error(error);
		}
	}

	@action.bound
	private setUserEventNotificationFavorites(notificationFavorites: string[]) {
		this.userFavoriteNotificationEventSet = new Set<string>(
			notificationFavorites
		);
	}

	@action.bound
	private async addUserEventNotificationFavorite(eventId: string) {
		this.userFavoriteNotificationEventSet.add(eventId);

		if (this._deleteBuffer.has(eventId)) {
			//this is double click, user deleted favorites and instantly clicked back to favorite it
			this._deleteBuffer.delete(eventId);
			return;
		}

		this._updateBuffer.add(eventId);

		await this.bufferUserNotificationFavorites();
	}

	@action.bound
	private async removeUserEventNotificationFavorite(eventId: string) {
		this.userFavoriteNotificationEventSet.delete(eventId);

		if (this._updateBuffer.has(eventId)) {
			//this is double click, user added favorite and instantly clicked to remove it
			this._updateBuffer.delete(eventId);
			return;
		}

		this._deleteBuffer.add(eventId);

		await this.bufferUserNotificationFavorites();
	}

	private async bufferUserNotificationFavorites() {
		if (!this.enableThrottling) {
			await this.handleBuffer();
			return;
		}

		clearTimeout(this._bufferTimeoutId);

		this._bufferTimeoutId = setTimeout(() => {
			this.handleBuffer();
		}, this.throttle);
	}

	private async handleBuffer() {
		const hasUpdates = !this.isBufferEmpty(this._updateBuffer);
		const hasDeletes = !this.isBufferEmpty(this._deleteBuffer);

		if (!hasDeletes && !hasUpdates) {
			//nothing to update
			return;
		}

		try {
			const updatePayload =
				hasUpdates && this.createValidPayload(this._updateBuffer);
			const deletePayload =
				hasDeletes && this.createValidPayload(this._deleteBuffer);

			this.clearUpdateBuffer();
			this.clearDeleteBuffer();

			const updateResponse =
				hasUpdates && updatePayload
					? await NotificationFavoritesApiService.updateNotificationFavorites(
							updatePayload
						)
					: null;

			const deleteResponse =
				hasDeletes && deletePayload
					? await NotificationFavoritesApiService.deleteNotificationFavorites(
							deletePayload
						)
					: null;

			let response: UserNotificationFavorites;
			if (deleteResponse) {
				response = deleteResponse;
			} else if (updateResponse) {
				response = updateResponse;
			}

			runInAction(() => {
				if (
					this.isBufferEmpty(this._updateBuffer) &&
					this.isBufferEmpty(this._deleteBuffer)
				) {
					this.setUserEventNotificationFavorites(
						response?.matchNotificationEvents || []
					);
				} else {
					this.handleBuffer();
				}
			});
		} catch (error) {
			console.error(error);
			if (
				error?.data?.errorCode === "400264" &&
				error?.data?.errorMessage
			) {
				//user doesnt have notification settings enabled
				this.userAuthStore.rootStore.accountVerificationStore.showPopup(
					"account-settings/notification-settings",
					"COMMON.NAVIGATION.NOTIFICATION_SETTINGS",
					error?.data?.errorMessage
				);
			}
			await this.getUserNotificationFavorites();
		}
	}

	private isBufferEmpty(buffer: Set<string>) {
		return buffer.size < 1;
	}

	private createValidPayload(buffer: Set<string>) {
		return {
			matchNotificationBettingTypes: [],
			matchNotificationEvents: [...buffer],
			matchNotificationPlayers: [],
			matchNotificationTeams: [],
			matchNotificationTournaments: [],
		};
	}

	private clearUpdateBuffer() {
		this._updateBuffer = new Set<string>();
	}

	private clearDeleteBuffer() {
		this._deleteBuffer = new Set<string>();
	}

	@action.bound
	public onDispose() {
		clearTimeout(this._bufferTimeoutId);
		this.clearDeleteBuffer();
		this.clearUpdateBuffer();
		this.userFavoriteNotificationEventSet = new Set<string>();
	}
}
